// Recycled strip for gallery thumbnails. Renders a fixed pool of elements
// (visible count + buffer) positioned by transform and rebound while
// scrolling, so the DOM never grows with the product's photo count. Scrolling
// is bounded: it stops hard at the first and last photo, and at the edges the
// wheel is handed back to the page. Exposes a thumbsSwiper-compatible
// surface: slideTo(index, speed), realIndex, update(). Falls back to a static
// strip when every thumb fits the container.
class EvelineThumbsRing {
  constructor(container, options) {
    this.container = container;
    this.files = options.files || [];
    this.total = this.files.length;
    this.createThumbPicture = options.createThumbPicture;
    this.onSelect = options.onSelect;
    this.offset = 0;
    this.activeIndex = 0;
    this.itemSize = 0;
    this.axis = 'v';
    this.scrollable = true;
    this.maxOffset = 0;
    this.pool = [];
    this._raf = null;
    this._pendingWheel = 0;
    this._wheelRaf = null;
    this._dragging = false;
    this._dragMoved = false;
    this._velocity = 0;
    this._prefetchStarted = false;

    this.container.classList.add('gallery__thumbs--ring');
    this._measure();
    this._bindInput();
    // Containers hidden at construction time measure 0x0; the observer fires
    // once they gain size (and again on breakpoint axis flips).
    this._resizeObserver = new ResizeObserver(() => this._measure());
    this._resizeObserver.observe(this.container);
    // Belt and braces: some activation paths reveal the container without a
    // resize notification - poll until the first successful measure.
    let retries = 0;
    const retry = () => {
      if (this.itemSize || retries++ > 40) return;
      this._measure();
      setTimeout(retry, 250);
    };
    setTimeout(retry, 250);
  }

  get realIndex() { return this.activeIndex; }

  update() { this._measure(); }

  _measure() {
    const rect = this.container.getBoundingClientRect();
    if (rect.width < 10 || rect.height < 10) return;
    const axis = rect.height >= rect.width ? 'v' : 'h';
    const itemSize = axis === 'v' ? rect.width : rect.height;
    // Use the axis detected NOW - this.axis may still hold the previous
    // orientation during a breakpoint flip, which shrank the pool badly.
    const length = axis === 'v' ? rect.height : rect.width;
    const poolCount = Math.min(this.total, Math.ceil(length / itemSize) + 3);
    const scrollable = this.total * itemSize > length + 1;
    const maxOffset = Math.max(0, this.total * itemSize - length);
    if (axis === this.axis && Math.abs(itemSize - this.itemSize) < 0.5 &&
        poolCount === this.pool.length && scrollable === this.scrollable) {
      this.maxOffset = maxOffset;
      return;
    }

    this.axis = axis;
    this.itemSize = itemSize;
    this.scrollable = scrollable;
    this.maxOffset = maxOffset;
    this.container.classList.toggle('is-vertical', axis === 'v');
    this.container.classList.toggle('is-horizontal', axis === 'h');
    // Horizontal strips ride inside a vertically scrolling page: let the
    // browser keep vertical panning, we consume horizontal drags (and the
    // other way round for vertical strips).
    this.container.style.touchAction = this.scrollable ? (axis === 'v' ? 'pan-x' : 'pan-y') : 'auto';
    this._buildPool(poolCount);
    this.slideTo(this.activeIndex, 0);
    this._startPrefetch();
  }

  _buildPool(count) {
    this.pool.forEach(el => el.remove());
    this.pool = [];
    for (let k = 0; k < count; k++) {
      const el = document.createElement('div');
      el.className = 'swiper-slide';
      const item = document.createElement('div');
      item.className = 'swiper-slide-item';
      el.appendChild(item);
      el._index = null;
      this.pool.push(el);
      this.container.appendChild(el);
    }
  }

  _clampIndex(i) { return Math.max(0, Math.min(i, this.total - 1)); }

  _clampOffset(v) { return Math.max(0, Math.min(v, this.maxOffset || 0)); }

  _bindItem(el, index) {
    el._index = index;
    const item = el.firstChild;
    while (item.firstChild) item.removeChild(item.firstChild);
    const file = this.files[index];
    if (!file) return;
    const picture = this.createThumbPicture(file);
    // Native image dragging swallows the pointer stream mid-gesture.
    picture.querySelectorAll('img').forEach(img => { img.draggable = false; });
    item.appendChild(picture);
  }

  _render() {
    if (!this.pool.length || !this.itemSize) return;
    if (!isFinite(this.offset)) this.offset = 0;
    this.offset = this._clampOffset(this.offset);
    const first = Math.max(0, Math.min(
      Math.floor(this.offset / this.itemSize) - 1,
      this.total - this.pool.length
    ));
    for (let k = 0; k < this.pool.length; k++) {
      const index = first + k;
      const el = this.pool[((index % this.pool.length) + this.pool.length) % this.pool.length];
      if (index < 0 || index >= this.total) {
        el.style.transform = 'translate3d(-9999px, -9999px, 0)';
        continue;
      }
      const pos = index * this.itemSize - this.offset;
      if (el._index !== index) this._bindItem(el, index);
      el.style.transform = this.axis === 'v'
        ? 'translate3d(0, ' + pos + 'px, 0)'
        : 'translate3d(' + pos + 'px, 0, 0)';
      el.classList.toggle('swiper-slide-thumb-active', index === this.activeIndex);
    }
  }

  slideTo(index, speed) {
    if (!this.total) return;
    this.activeIndex = this._clampIndex(index);
    // Not measured yet (container hidden at init): remember the index only,
    // _measure() re-runs slideTo once the strip has real dimensions.
    if (!this.itemSize) return;
    if (!isFinite(this.offset)) this.offset = 0;
    this._stopMotion();
    // Place the active thumb one slot below the strip start (mirrors the old
    // swiper slideTo feel), clamped to the strip's bounds.
    const targetOffset = this.scrollable
      ? this._clampOffset((this.activeIndex - 1) * this.itemSize)
      : 0;
    if (!speed) {
      this.offset = targetOffset;
      this._render();
      return;
    }
    const from = this.offset;
    const dist = targetOffset - from;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / speed);
      this.offset = from + dist * (1 - Math.pow(1 - p, 3));
      this._render();
      if (p < 1) this._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  }

  _stopMotion() {
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
    this._velocity = 0;
  }

  _bindInput() {
    this.container.addEventListener('dragstart', (event) => event.preventDefault());

    this.container.addEventListener('wheel', (event) => {
      if (!this.scrollable) return;
      const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      const current = this._clampOffset(this.offset + this._pendingWheel);
      const next = this._clampOffset(current + delta);
      // At the strip's edge the event is not consumed - the page scrolls.
      if (next === current) return;
      event.preventDefault();
      this._pendingWheel += delta;
      if (this._wheelRaf) return;
      this._wheelRaf = requestAnimationFrame(() => {
        this._wheelRaf = null;
        this._stopMotion();
        this.offset = this._clampOffset(this.offset + this._pendingWheel);
        this._pendingWheel = 0;
        this._render();
      });
    }, { passive: false });

    this.container.addEventListener('pointerdown', (event) => {
      // Remember the pressed slide now: setPointerCapture retargets every
      // later event (click included) to the container itself.
      this._downSlide = event.target.closest ? event.target.closest('.swiper-slide') : null;
      if (!this.scrollable) return;
      this._dragging = true;
      this._dragMoved = false;
      this._stopMotion();
      this._startPos = this.axis === 'v' ? event.clientY : event.clientX;
      this._lastPos = this._startPos;
      this._lastTime = performance.now();
      this._velocity = 0;
      this.container.setPointerCapture(event.pointerId);
    });

    this.container.addEventListener('pointermove', (event) => {
      if (!this._dragging) return;
      const pos = this.axis === 'v' ? event.clientY : event.clientX;
      // Dead zone: trackpad taps jitter a few pixels - the strip must not
      // move and the tap must stay a click until the pointer really travels.
      if (!this._dragMoved) {
        if (Math.abs(pos - this._startPos) < 8) return;
        this._dragMoved = true;
        this._lastPos = pos;
        this._lastTime = performance.now();
        return;
      }
      const delta = pos - this._lastPos;
      const now = performance.now();
      const dt = Math.max(1, now - this._lastTime);
      this._velocity = 0.8 * this._velocity + 0.2 * (-delta / dt);
      this._lastPos = pos;
      this._lastTime = now;
      this.offset = this._clampOffset(this.offset - delta);
      this._render();
    });

    const endDrag = () => {
      if (!this._dragging) return;
      this._dragging = false;
      if (Math.abs(this._velocity) > 0.05) this._startMomentum();
    };
    this.container.addEventListener('pointerup', endDrag);
    this.container.addEventListener('pointercancel', endDrag);

    this.container.addEventListener('click', (event) => {
      const slide = (event.target.closest && event.target.closest('.swiper-slide')) || this._downSlide;
      this._downSlide = null;
      if (this._dragMoved) { this._dragMoved = false; return; }
      if (slide && typeof slide._index === 'number' && this.onSelect) this.onSelect(slide._index);
    });
  }

  _startMomentum() {
    let last = performance.now();
    const step = (now) => {
      const dt = now - last;
      last = now;
      const next = this._clampOffset(this.offset + this._velocity * dt);
      const hitEdge = next === this.offset && this._velocity !== 0;
      this.offset = next;
      this._velocity = hitEdge ? 0 : this._velocity * Math.pow(0.94, dt / 16.7);
      this._render();
      if (Math.abs(this._velocity) > 0.02) this._raf = requestAnimationFrame(step);
      else this._raf = null;
    };
    this._raf = requestAnimationFrame(step);
  }

  // Warm the browser cache for every thumb so ring rebinds render instantly.
  // Runs once, on idle, a few downloads at a time, spreading out from the
  // active index; sizes are cloned from the first really rendered thumb.
  _startPrefetch() {
    if (this._prefetchStarted || !this.total) return;
    const sample = this.container.querySelector('img');
    const src = sample && (sample.currentSrc || sample.src);
    const match = src && src.match(/_(\d+)x\./);
    if (!match) return;
    this._prefetchStarted = true;
    const width = parseInt(match[1], 10);
    const order = [];
    for (let d = 0; order.length < this.total && d < this.total * 2; d++) {
      const i = this.activeIndex + Math.ceil(d / 2) * (d % 2 ? 1 : -1);
      if (i >= 0 && i < this.total) order.push(i);
    }
    const urls = order
      .map(i => this.files[i] && this.files[i].src)
      .filter(s => typeof s === 'string')
      .map(s => s.replace(/(\.[a-z]+)(\?|$)/i, '_' + width + 'x$1$2'));
    let cursor = 0;
    const pump = () => {
      while (cursor < urls.length && pump.active < 6) {
        const img = new Image();
        pump.active++;
        img.onload = img.onerror = () => { pump.active--; pump(); };
        img.src = urls[cursor++];
      }
    };
    pump.active = 0;
    const kickoff = () => pump();
    if ('requestIdleCallback' in window) requestIdleCallback(kickoff, { timeout: 4000 });
    else setTimeout(kickoff, 2500);
  }
}

class EvelineProductGallery extends HTMLElement {
  constructor() {
    super();
    this.galleryData = null;
    this.swiperInstances = new Map();
    this.videosMuted = true;
    this.youtubePlayers = new Map();
  }

  connectedCallback() {
    this.init();
    
    // Listen for color switcher option changes
    document.addEventListener('option-change', (event) => {
      this.handleOptionChange(event.detail);
    });
  }

  init() {
    // Decode and parse gallery data from base64
    if (window.product_gallery_data) {
      try {
        this.galleryData = JSON.parse(atob(window.product_gallery_data));
      } catch (e) {
        console.error('Failed to decode gallery data:', e);
        return;
      }
    } else {
      console.error('Gallery data not found');
      return;
    }

    // Set initial mute state based on config
    if (this.galleryData.config && this.galleryData.config.video_autoplay_with_sound) {
      this.videosMuted = false;
    }

    // Initialize default gallery - create first gallery immediately in DOM
    this.initDefaultGallery();
  }

  initDefaultGallery() {
    // Check URL for variant, fallback to default selected variant
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant') || this.getAttribute('data-selected-variant-id');

    let gallery;
    let slideIndex = null;
    const lang = this.galleryData.config?.lang || 'unknown';
    const isSingleOption = this.galleryData.config?.single_option_product === 1;
    
    if (variantId && this.galleryData.gallery_variants && this.galleryData.gallery_variants[variantId]) {
      // Create variant gallery immediately
      const files = this.galleryData.gallery_variants[variantId].files;
      console.log(`Init: Variant gallery ${variantId} (${lang}) found, activated`);
      gallery = this.createGallery('variant', files, variantId);
      this.setAttribute('data-items-number', files.length);
      this.setAttribute('data-current-gallery-size', files.length);
    } else if (this.galleryData.gallery_lang && this.galleryData.gallery_lang.files.length > 0) {
      // Create lang gallery immediately
      const files = this.galleryData.gallery_lang.files;
      console.log(`Init: gallery_lang (${lang}) found, activated`);
      gallery = this.createGallery('lang', files);
      this.setAttribute('data-items-number', files.length);
      this.setAttribute('data-current-gallery-size', files.length);
      
      // If variant in URL but no gallery_variants, try to find slide for single option product
      if (variantId && isSingleOption) {
        slideIndex = this.findSlideIndexByVariantId('lang', variantId);
        if (slideIndex !== -1) {
          console.log(`Init: Single option product, found variant image at index ${slideIndex} in gallery_lang`);
        }
      }
    } else {
      // Create featured gallery immediately
      const files = this.galleryData.gallery_featured.files;
      console.log(`Init: gallery_featured (${lang}) activated`);
      gallery = this.createGallery('featured', files);
      this.setAttribute('data-items-number', files.length);
      this.setAttribute('data-current-gallery-size', files.length);
      
      // If variant in URL but no gallery_variants, try to find slide for single option product
      if (variantId && isSingleOption) {
        slideIndex = this.findSlideIndexByVariantId('featured', variantId);
        if (slideIndex !== -1) {
          console.log(`Init: Single option product, found variant image at index ${slideIndex} in gallery_featured`);
        }
      }
    }
    
    // Mark first gallery as active
    if (gallery) {
      gallery.classList.add('is-active');
      this.classList.add('gallery-ready');
      
      // If we found a specific slide index, scroll to it after swiper is initialized
      if (slideIndex !== null && slideIndex >= 0) {
        this.scrollToSlideOnInit(gallery, slideIndex);
      }
    }
  }
  
  scrollToSlideOnInit(gallery, slideIndex) {
    // Find the gallery type and key
    const galleryKey = gallery.getAttribute('data-gallery-featured') ? '1' 
      : (gallery.getAttribute('data-gallery-lang') ? '1' 
      : gallery.getAttribute('data-gallery-variant'));
    
    // Wait for swiper to be initialized, then scroll
    setTimeout(() => {
      const swiperInstance = this.swiperInstances.get(galleryKey);
      if (swiperInstance) {
        swiperInstance.main.slideTo(slideIndex, 0);
        swiperInstance.thumbs.slideTo(slideIndex, 0);
        console.log(`Init: Scrolled to slide ${slideIndex}`);
      }
    }, 100);
  }

  showFeaturedGallery() {
    this.activateGallery('featured');
  }

  showLangGallery() {
    this.activateGallery('lang');
  }

  findSlideIndexByVariantId(galleryType, variantId) {
    // Find slide with matching variant_id in the specified gallery
    const gallerySelector = galleryType === 'featured' 
      ? '[data-gallery-featured="1"]' 
      : '[data-gallery-lang="1"]';
    
    const gallery = this.querySelector(gallerySelector);
    if (!gallery) return -1;

    // Search through ALL slides (including those without variant_id)
    const allSlides = gallery.querySelectorAll('.swiper-slide-item');
    for (let i = 0; i < allSlides.length; i++) {
      const slideVariantId = allSlides[i].getAttribute('data-single-image-variant-id');
      if (slideVariantId && slideVariantId === String(variantId)) {
        console.log(`Found variant ${variantId} at slide index ${i}`);
        return i;
      }
    }
    
    console.log(`Variant ${variantId} not found in any slide`);
    return -1;
  }

  showVariantGallery(variantId) {
    if (!this.galleryData.gallery_variants || !this.galleryData.gallery_variants[variantId]) {
      // Variant gallery not found - try fallback
      const lang = this.galleryData.config?.lang || 'unknown';
      const isSingleOption = this.galleryData.config?.single_option_product === 1;
      
      if (this.galleryData.gallery_lang && this.galleryData.gallery_lang.files && this.galleryData.gallery_lang.files.length > 0) {
        console.log(`Variant gallery ${variantId} not found, found gallery_lang (${lang}), switched`);
        this.activateGallery('lang');
        return;
      } else {
        const lang = this.galleryData.config?.lang || 'unknown';
        console.log(`Variant gallery ${variantId} not found, not found gallery_lang, switched to gallery_featured (${lang})`);
        
        // If single option product, try to find and scroll to variant slide
        if (isSingleOption) {
          const slideIndex = this.findSlideIndexByVariantId('featured', variantId);
          if (slideIndex !== -1) {
            console.log(`Single option product: found variant image at index ${slideIndex}, scrolling`);
            this.activateGallery('featured', null, slideIndex);
            return;
          }
        }
        
        this.activateGallery('featured');
        return;
      }
    }
    const lang = this.galleryData.config?.lang || 'unknown';
    console.log(`Variant gallery ${variantId} (${lang}) found, switched`);
    this.activateGallery('variant', variantId);
  }
  
  
  // Get decoded product variants data
  getProductVariants() {
    if (this._productVariants) {
      return this._productVariants;
    }
    
    if (!window.product_variants) {
      console.warn('window.product_variants not found');
      return null;
    }
    
    try {
      this._productVariants = JSON.parse(atob(window.product_variants));
      return this._productVariants;
    } catch (error) {
      console.error('Failed to decode product_variants:', error);
      return null;
    }
  }
  
  // Get currently selected option values
  getSelectedOptions() {
    const variantsData = this.getProductVariants();
    if (!variantsData) return {};
    
    const selectedOptions = {};
    const productOptions = document.querySelector('product-options-element');
    
    if (!productOptions) {
      // If no product-options-element, try to get from current variant
      const currentVariantId = this.getAttribute('data-selected-variant-id');
      if (currentVariantId && variantsData.variants[currentVariantId]) {
        return variantsData.variants[currentVariantId];
      }
      return {};
    }
    
    // Get each option value
    Object.keys(variantsData.options).forEach(optionKey => {
      const optionName = variantsData.options[optionKey];
      const optionNameLc = String(optionName).trim().toLowerCase();
      let value = null;

      // 1) Value currently selected in color switcher
      document.querySelectorAll('eveline-color-switcher').forEach(sw => {
        if (value != null) return;
        const swName = (sw.getAttribute('data-option-name') || '').trim().toLowerCase();
        if (swName !== optionNameLc) return;
        const selected = sw.querySelector('.switch-grid-option.is-selected, .switch-list-option.is-selected');
        if (selected) value = selected.getAttribute('data-option-value');
      });

      // 2) Native radio / swatch input (exposes data-option-name)
      if (value == null) {
        const radioInput = productOptions.querySelector(
          `input[type="radio"][data-option-name="${optionNameLc}"]:checked`
        );
        if (radioInput) {
          value = radioInput.value;
        } else {
          const selectInput = productOptions.querySelector(`select[name="${optionName}"]`);
          if (selectInput) value = selectInput.value;
        }
      }

      if (value != null) selectedOptions[optionKey] = value;
    });

    return selectedOptions;
  }
  
  // Find variant ID that matches given options
  findMatchingVariant(targetOptions) {
    const variantsData = this.getProductVariants();
    if (!variantsData) return null;
    
    // Iterate through all variants to find match
    for (const [variantId, variantOptions] of Object.entries(variantsData.variants)) {
      let isMatch = true;
      
      // Check if all target options match this variant
      for (const [optionKey, optionValue] of Object.entries(targetOptions)) {
        if (variantOptions[optionKey] !== optionValue) {
          isMatch = false;
          break;
        }
      }
      
      if (isMatch) {
        return variantId;
      }
    }
    
    return null;
  }
  
  handleOptionChange(detail) {
    const { optionName, value, variantId: directVariantId } = detail;

    const variantsData = this.getProductVariants();
    if (!variantsData) return;

    if (
      directVariantId &&
      variantsData.variants &&
      variantsData.variants[directVariantId] &&
      Object.keys(variantsData.options).length === 1
    ) {
      this.changeProductVariant(directVariantId);
      return;
    }

    // Get current selected options
    const currentOptions = this.getSelectedOptions();
    
    // Find which option key corresponds to this option name
    let optionKey = null;
    for (const [key, name] of Object.entries(variantsData.options)) {
      if (name === optionName) {
        optionKey = key;
        break;
      }
    }
    
    if (!optionKey) {
      console.warn('Option not found:', optionName);
      return;
    }
    
    // Update the changed option
    currentOptions[optionKey] = value;
    
    // Find matching variant
    const variantId = this.findMatchingVariant(currentOptions);
    
    if (variantId) {
      this.changeProductVariant(variantId);
    } else {
      console.warn('No matching variant found for options:', currentOptions);
    }
  }
  
  // Gallery -> variant UI: when the active slide belongs to a variant, mirror
  // it into the color switcher, buy buttons and URL. Guarded against loops:
  // variant-driven jumps set data-selected-variant-id before their slideTo.
  syncVariantFromSlide(gallery, slideIndex) {
    const file = (gallery._files || [])[slideIndex];
    const variantId = file && file.variant_id;
    if (!variantId) return;
    if (String(this.getAttribute('data-selected-variant-id')) === String(variantId)) return;
    document.querySelectorAll('eveline-color-switcher').forEach(sw => {
      const opt = sw.querySelector('.switch-grid-option[data-variant="' + variantId + '"], .switch-list-option[data-variant="' + variantId + '"]');
      if (opt && typeof sw.updateSelection === 'function') {
        const value = opt.getAttribute('data-option-value');
        if (value) sw.updateSelection(value);
      }
    });
    this.changeProductVariant(variantId, true);
  }

  changeProductVariant(variantId, skipGallery) {
    // Show variant gallery (skipped when the change originates from the
    // gallery itself - the user is already looking at the slide they chose)
    if (!skipGallery) this.showVariantGallery(variantId);
    
    // Get full variant data
    const variantsData = this.getProductVariants();
    let variant = null;
    
    if (variantsData && variantsData.variants) {
      // variants is an object with variant IDs as keys
      variant = variantsData.variants[variantId];
    }

    // Update the hidden variant ID input
    const variantInput = document.querySelector('.product-buy-buttons--input');
    if (variantInput) {
      variantInput.value = variantId;
      
      // Trigger change event so other components can react
      const event = new Event('change', { bubbles: true });
      variantInput.dispatchEvent(event);
      
      // Update data attribute
      this.setAttribute('data-selected-variant-id', variantId);
    } else {
      console.warn('.product-buy-buttons--input not found');
    }
    
    // Emit theme:product:variantChanged event for product-buy-buttons-element
    if (variant) {
      const buyButtonsElement = document.querySelector('product-buy-buttons-element');
      if (buyButtonsElement) {
        // Update data-variant-available attribute
        buyButtonsElement.setAttribute('data-variant-available', variant.available ? '1' : '0');
        
        const productId = buyButtonsElement.getAttribute('data-id');
        if (productId) {
          window.dispatchEvent(new CustomEvent(`theme:product:${productId}:variantChanged`, {
            detail: {
              variant: variant
            }
          }));
        }
      }
    }
    
    this.updateUrlWithVariant(variantId);
  }
  
  updateUrlWithVariant(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    
    // Update URL without reloading the page
    window.history.replaceState({ variantId: variantId }, '', url.toString());
  }

  pauseAllVideos() {
    // Find all YouTube iframes and mute them (don't pause, let autoplay work)
    const youtubeIframes = this.querySelectorAll('iframe[src*="youtube.com"]');
    youtubeIframes.forEach(iframe => {
      iframe.contentWindow.postMessage('{"event":"command","func":"mute","args":""}', '*');
    });

    // Find all Vimeo iframes
    const vimeoIframes = this.querySelectorAll('iframe[src*="vimeo.com"]');
    vimeoIframes.forEach(iframe => {
      iframe.contentWindow.postMessage('{"method":"pause"}', '*');
    });

    // Find all HTML5 video elements
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      video.pause();
    });
  }

  activateGallery(type, variantId = null, slideIndex = null) {
    // Pause all videos first
    this.pauseAllVideos();
    let gallery;
    let itemsCount = 0;
    
    if (type === 'featured') {
      itemsCount = this.galleryData.gallery_featured.files.length;
      gallery = this.querySelector('[data-gallery-featured="1"]');
      if (!gallery) {
        gallery = this.createGallery('featured', this.galleryData.gallery_featured.files);
      }
    } else if (type === 'lang') {
      itemsCount = this.galleryData.gallery_lang.files.length;
      gallery = this.querySelector('[data-gallery-lang="1"]');
      if (!gallery) {
        gallery = this.createGallery('lang', this.galleryData.gallery_lang.files);
      }
    } else if (type === 'variant' && variantId) {
      itemsCount = this.galleryData.gallery_variants[variantId].files.length;
      gallery = this.querySelector(`[data-gallery-variant="${variantId}"]`);
      if (!gallery) {
        gallery = this.createGallery('variant', this.galleryData.gallery_variants[variantId].files, variantId);
      }
    }

    // Remove is-active from all galleries
    this.querySelectorAll('.gallery').forEach(g => g.classList.remove('is-active'));
    
    // Add is-active to current gallery
    if (gallery) {
      gallery.classList.add('is-active');
      this.classList.add('gallery-ready');
      
      // Update items number for skeleton
      this.setAttribute('data-items-number', itemsCount);
      this.setAttribute('data-current-gallery-size', itemsCount);
      
      // Scroll thumbs to active slide when switching galleries
      const galleryKey = type === 'featured' ? '1' : (type === 'lang' ? '1' : variantId);
      const swiperInstance = this.swiperInstances.get(galleryKey);
      if (swiperInstance) {
        setTimeout(() => {
          // The swipers may have initialized while the container was hidden
          // (size 0 -> watchOverflow locks navigation and snapGrid collapses).
          // The gallery is visible now, so re-measure before any slideTo.
          swiperInstance.main.update();
          swiperInstance.thumbs.update();

          // If slideIndex provided, scroll to that slide
          if (slideIndex !== null && slideIndex >= 0) {
            swiperInstance.main.slideTo(slideIndex, 0);
            swiperInstance.thumbs.slideTo(slideIndex, 0);
          } else {
            swiperInstance.thumbs.slideTo(swiperInstance.main.realIndex, 0);
          }
          
          // Handle video autoplay with sound if gallery is active
          this.handleVideoAutoplay(gallery, swiperInstance.main);
        }, 50);
      }
    }
  }

  handleVideoAutoplay(gallery, swiper) {
    const autoplayWithSound = this.galleryData.config?.video_autoplay_with_sound;
    
    // First, pause ALL videos in ALL slides (including duplicates from loop mode)
    swiper.slides.forEach((slide) => {
      const youtubeIframe = slide.querySelector('iframe[src*="youtube.com"]');
      if (youtubeIframe && youtubeIframe.contentWindow) {
        youtubeIframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      }
      
      const vimeoIframe = slide.querySelector('iframe[src*="vimeo.com"]');
      if (vimeoIframe && vimeoIframe.contentWindow) {
        vimeoIframe.contentWindow.postMessage('{"method":"pause"}', '*');
      }
      
      const video = slide.querySelector('video');
      if (video) {
        video.pause();
      }
    });
    
    // Don't play if gallery is not active
    const galleryIsActive = gallery.classList.contains('is-active');
    if (!galleryIsActive) return;
    
    // Don't play if current displayed slide is a duplicate (loop mode)
    const currentDisplayedSlide = swiper.slides[swiper.activeIndex];
    if (!currentDisplayedSlide || currentDisplayedSlide.classList.contains('swiper-slide-duplicate')) {
      return;
    }
    
    // Get only original slides (not duplicates)
    const originalSlides = Array.from(swiper.slides).filter(slide => 
      !slide.classList.contains('swiper-slide-duplicate')
    );
    
    // Get the actual active slide by realIndex
    const activeSlide = originalSlides[swiper.realIndex];
    if (!activeSlide) return;
    
    // Check if active slide has YouTube video
    const youtubeIframe = activeSlide.querySelector('iframe[src*="youtube.com"]');
    if (youtubeIframe && youtubeIframe.contentWindow) {
      // Play YouTube video - respect both autoplayWithSound and videosMuted states
      if (autoplayWithSound && !this.videosMuted) {
        youtubeIframe.contentWindow.postMessage('{"event":"command","func":"unMute","args":""}', '*');
      } else {
        youtubeIframe.contentWindow.postMessage('{"event":"command","func":"mute","args":""}', '*');
      }
      youtubeIframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      return;
    }
    
    // Check if active slide has Shopify video
    const video = activeSlide.querySelector('video');
    if (video) {
      // Set mute state and play - respect both autoplayWithSound and videosMuted states
      video.muted = !(autoplayWithSound && !this.videosMuted);
      video.play().catch(err => console.log('Video autoplay failed:', err));
    }
  }

  createGallery(type, files, variantId = null) {
    const gallery = document.createElement('div');
    gallery.className = 'gallery';
    
    // Set data attribute
    if (type === 'featured') {
      gallery.setAttribute('data-gallery-featured', '1');
    } else if (type === 'lang') {
      gallery.setAttribute('data-gallery-lang', '1');
    } else if (type === 'variant') {
      gallery.setAttribute('data-gallery-variant', variantId);
    }
    
    // Set gallery size
    gallery.setAttribute('data-gallery-size', files.length);

    // Keep files for the lightbox
    gallery._files = files;

    // Windowed rendering for heavy galleries: only slides near the active index
    // get a real <picture>; the rest hold a fixed-size placeholder until needed.
    // Building 2x200+ images at once starves the main thread on mobile Safari
    // (every iOS URL-bar resize relayouts all of them) and gets the page killed.
    gallery._windowed = files.length > 60;

    // Create main and thumbs containers
    const mainContainer = document.createElement('div');
    mainContainer.className = 'gallery__main swiper';
    
    const thumbsContainer = document.createElement('div');
    thumbsContainer.className = 'gallery__thumbs swiper';

    // Build main swiper
    const mainWrapper = document.createElement('div');
    mainWrapper.className = 'swiper-wrapper';
    
    files.forEach(file => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      
      const slideItem = document.createElement('div');
      slideItem.className = 'swiper-slide-item';
      
      // Add variant_id attribute if product has single option
      if (this.galleryData.config?.single_option_product === 1 && file.variant_id) {
        slideItem.setAttribute('data-single-image-variant-id', file.variant_id);
        if (file.option_name) {
          slideItem.setAttribute('data-single-image-option-name', file.option_name);
        }
      }
      
      const videoSources = file.sources || file.src?.sources;
      if (file.media_type === 'image' || (typeof file.src === 'string' && file.src)) {
        if (gallery._windowed) {
          slideItem.appendChild(this.createSlidePlaceholder());
        } else {
          const picture = this.createPictureElement(file, 'main');
          // Native lazy-load proved unreliable inside the translated swiper
          // wrapper (in-viewport images never fire); light galleries can
          // afford loading all slides up front.
          picture.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
          slideItem.appendChild(picture);
        }
      } else if (file.media_type === 'video' && videoSources) {
        // Shopify native video
        const videoWrapper = document.createElement('div');
        videoWrapper.className = 'video-wrapper';
        videoWrapper.style.position = 'relative';

        const video = document.createElement('video');
        const videoControls = this.galleryData.config?.video_controls !== false;
        video.controls = videoControls;
        video.playsInline = true;
        video.autoplay = true; // Enable autoplay
        video.loop = true; // Loop the video
        video.muted = true; // Start muted (required for autoplay)
        video.style.width = '100%';
        video.style.height = '100%';
        const previewSrc = file.preview_image?.src || file.src?.preview_image?.src;
        if (previewSrc) {
          video.poster = previewSrc;
        }

        videoSources.forEach(source => {
          const sourceEl = document.createElement('source');
          sourceEl.src = source.url;
          sourceEl.type = source.mime_type;
          video.appendChild(sourceEl);
        });
        
        videoWrapper.appendChild(video);
        slideItem.appendChild(videoWrapper);
      } else if (file.host && file.external_id) {
        // External video (YouTube, Vimeo)
        const videoWrapper = document.createElement('div');
        videoWrapper.className = 'video-wrapper';
        videoWrapper.style.position = 'relative';
        
        const iframe = this.createVideoIframe(file.host, file.external_id);
        videoWrapper.appendChild(iframe);
        
        slideItem.appendChild(videoWrapper);
      }
      
      slide.appendChild(slideItem);
      mainWrapper.appendChild(slide);
    });

    mainContainer.appendChild(mainWrapper);

    // Add navigation buttons
    const prevButton = document.createElement('div');
    prevButton.className = 'swiper-button-prev';
    prevButton.innerHTML = '<svg role="presentation" class="icon icon--chevron-left" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34.785 54.68" width="34.785" height="54.68"><path d="M34.042 53.296a2.449 2.449 0 0 1-3.428.49L1.229 29.299a2.449 2.449 0 0 1 0-3.918L30.614.894a2.449 2.449 0 1 1 2.936 3.918L6.781 27.34 33.55 49.868a2.449 2.449 0 0 1 .492 3.428Z" fill="currentColor" stroke="currentColor" stroke-width=".5" fill-rule="evenodd"></path></svg>';
    
    const nextButton = document.createElement('div');
    nextButton.className = 'swiper-button-next';
    nextButton.innerHTML = '<svg role="presentation" class="icon icon--chevron-right" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 34.785 54.68" width="34.785" height="54.68"><path d="M.743 53.296a2.449 2.449 0 0 0 3.428.49l29.382-24.487a2.449 2.449 0 0 0 0-3.918L4.171.894a2.449 2.449 0 1 0-2.938 3.918L28.004 27.34 1.233 49.868a2.449 2.449 0 0 0-.49 3.428Z" fill="currentColor" stroke="currentColor" stroke-width=".5" fill-rule="evenodd"></path></svg>';
    
    mainContainer.appendChild(prevButton);
    mainContainer.appendChild(nextButton);
    
    // Add toggle mute button once for the entire gallery (if any videos and enabled)
    const hasVideos = files.some(file => file.host === 'youtube' || file.media_type === 'video');
    if (hasVideos && this.galleryData.config?.video_show_sound_toggle_btn) {
      const toggleBtn = this.createToggleMuteButton();
      mainContainer.appendChild(toggleBtn);
    }

    // Thumbs strip is a bounded recycled ring (EvelineThumbsRing, created in
    // initSwiper) - no per-file DOM is built here.

    // Append to gallery - thumbs position will be handled by CSS based on screen size
    gallery.appendChild(mainContainer);
    gallery.appendChild(thumbsContainer);

    // Append to gallery-wrapper container instead of directly to component
    const galleryWrapper = this.querySelector('.gallery-wrapper');
    if (galleryWrapper) {
      galleryWrapper.appendChild(gallery);
    } else {
      console.error('.gallery-wrapper not found');
      this.appendChild(gallery);
    }

    // Initialize Swiper
    this.initSwiper(gallery, mainContainer, thumbsContainer);

    return gallery;
  }

  initSwiper(gallery, mainContainer, thumbsContainer) {
    if (typeof Swiper === 'undefined') {
      console.error('Swiper not loaded');
      return;
    }
    
    // Get config from gallery data
    const config = this.galleryData.config || {};
    const loop = config.loop !== undefined ? config.loop : false;
    const autoplay = config.autoplay !== undefined ? config.autoplay : false;
    const autoplayDelay = config.autoplay_delay || 3000;
    const speed = config.animation_duration || 300;
    const disableOnInteraction = config.disableOnInteraction !== undefined ? config.disableOnInteraction : false;

    // Store reference to this for use in callbacks
    const self = this;

    // Thumbs strip: bounded recycled ring instead of a Swiper instance.
    const ringFiles = (gallery._files || []).map(file => ({
      src: file.preview_image?.src || file.src,
      alt: file.alt
    }));
    const thumbsRing = new EvelineThumbsRing(thumbsContainer, {
      files: ringFiles,
      createThumbPicture: (file) => {
        const picture = self.createPictureElement(file, 'thumbs');
        // The ring itself decides what is rendered - native lazy-load would
        // only delay thumbs that are already on screen.
        picture.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
        return picture;
      },
      onSelect: (index) => {
        // A swiper initialized while hidden measures size 0 and watchOverflow
        // locks navigation, which makes slideTo silently refuse - re-measure
        // before jumping if that state is detected.
        if (mainSwiper.isLocked || !mainSwiper.allowSlideNext || !mainSwiper.allowSlidePrev) {
          mainSwiper.update();
        }
        mainSwiper.slideTo(index);
      }
    });
    
    // Get gallery size to adjust slidesPerView for single-slide galleries
    const gallerySize = parseInt(gallery.getAttribute('data-gallery-size')) || 0;
    const mobileSlidesPerView = gallerySize === 1 ? 1 : 1.33;

    // Initialize main swiper
    const mainSwiper = new Swiper(mainContainer, {
      spaceBetween: 0,
      loop: loop,
      loopAdditionalSlides: 0,
      speed: speed,
      slidesPerView: 1,
      centeredSlides: false,
      autoplay: autoplay ? {
        delay: autoplayDelay,
        disableOnInteraction: disableOnInteraction,
      } : false,
      navigation: {
        nextEl: mainContainer.querySelector('.swiper-button-next'),
        prevEl: mainContainer.querySelector('.swiper-button-prev'),
      },
      breakpoints: {
        0: {
          slidesPerView: mobileSlidesPerView,
          centeredSlides: false
        },
        600: {
          slidesPerView: 1,
          centeredSlides: false
        }
      },
      on: {
        init: function() {
          // Remove iframes from duplicate slides immediately on init
          if (loop) {
            self.cleanDuplicateIframes(this);
          }
        },
        loopFix: function() {
          // Called when loop is fixed/recreated
          if (loop) {
            self.cleanDuplicateIframes(this);
          }
        },
        slideChangeTransitionStart: function() {
          // Remove iframes before transition starts
          if (loop) {
            self.cleanDuplicateIframes(this);
          }
        },
        slideChangeTransitionEnd: function() {
          // Safety: remove iframes after transition ends
          if (loop) {
            self.cleanDuplicateIframes(this);
          }
        },
        transitionEnd: function() {
          // Additional safety callback
          if (loop) {
            self.cleanDuplicateIframes(this);
          }
        }
      }
    });
    
    // Add MutationObserver to aggressively remove iframes from duplicates
    if (loop) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Check if added node or its children are duplicate slides with iframes
              if (node.classList && node.classList.contains('swiper-slide-duplicate')) {
                const iframes = node.querySelectorAll('iframe');
                iframes.forEach(iframe => iframe.remove());
              }
              // Also check children
              const duplicates = node.querySelectorAll('.swiper-slide-duplicate');
              duplicates.forEach(duplicate => {
                const iframes = duplicate.querySelectorAll('iframe');
                iframes.forEach(iframe => iframe.remove());
              });
            }
          });
        });
      });
      
      observer.observe(mainContainer, {
        childList: true,
        subtree: true
      });
      
      // Store observer for cleanup
      this.observers = this.observers || new Map();
      this.observers.set(gallery, observer);
    }

    // A hidden-at-init container measures 0 and poisons the swiper (locked
    // navigation, collapsed snapGrid); re-measure whenever the container's
    // size actually changes instead of guessing with timers.
    const mainSizeObserver = new ResizeObserver(() => {
      if (mainSwiper && !mainSwiper.destroyed) mainSwiper.update();
    });
    mainSizeObserver.observe(mainContainer);

    // Open lightbox on tap/click of an image slide (swiper 'click' ignores drags)
    mainSwiper.on('click', function() {
      const slide = this.clickedSlide;
      if (!slide || !slide.querySelector('picture')) return;

      let slideIndex = parseInt(slide.getAttribute('data-swiper-slide-index') || '', 10);
      if (isNaN(slideIndex)) {
        const originals = Array.from(this.slides).filter(s => !s.classList.contains('swiper-slide-duplicate'));
        slideIndex = originals.indexOf(slide);
      }
      if (slideIndex < 0) slideIndex = this.realIndex;

      self.openLightbox(gallery, mainSwiper, slideIndex);
    });

    mainSwiper.on('slideChange', function() {
      // Windowed gallery: make sure slides around the new position have content
      self.populateSlideWindow(gallery, this.realIndex);

      // Mirror the shown slide's variant into the dropdown/grid/URL
      self.syncVariantFromSlide(gallery, this.realIndex);

      // Use realIndex to handle loop mode correctly
      thumbsRing.slideTo(this.realIndex, speed);
      
      // Update toggle button visibility
      self.updateToggleMuteButtonState(gallery, mainSwiper);
      
      // Handle video autoplay with sound
      self.handleVideoAutoplay(gallery, this);
    });

    // Initial scroll to active thumb on load
    setTimeout(() => {
      thumbsRing.slideTo(mainSwiper.realIndex, 0);
      
      // Initial button state
      self.updateToggleMuteButtonState(gallery, mainSwiper);
      
      // Initial autoplay handling
      self.handleVideoAutoplay(gallery, mainSwiper);

      // Windowed gallery: initial content around the starting position.
      if (gallery._windowed) {
        self.populateSlideWindow(gallery, mainSwiper.realIndex);
        requestAnimationFrame(() => {
          // Windowed galleries have no images at init, so imagesReady never
          // re-measures the swiper - do it after the initial population.
          // (activateGallery also updates on every activation, which covers
          // containers that were hidden during initialization.)
          mainSwiper.update();
        });
      }
    }, 100);

    // Store swiper instances
    const galleryKey = gallery.getAttribute('data-gallery-featured') || 
                       gallery.getAttribute('data-gallery-lang') || 
                       gallery.getAttribute('data-gallery-variant');
    this.swiperInstances.set(galleryKey, { main: mainSwiper, thumbs: thumbsRing });
  }
  
  cleanDuplicateIframes(swiper) {
    const duplicates = Array.from(swiper.slides).filter(s => s.classList.contains('swiper-slide-duplicate'));
    duplicates.forEach(slide => {
      const iframes = slide.querySelectorAll('iframe');
      iframes.forEach(iframe => iframe.remove());
    });
  }

  createSlidePlaceholder() {
    const ph = document.createElement('div');
    ph.className = 'gallery-slide-ph';
    return ph;
  }

  // Swap a slide's placeholder for its real <picture>. Idempotent: does nothing
  // for already-populated slides and for non-image slides (video/external).
  populateSlide(slide, file, type) {
    if (!slide || !file) return false;
    const item = slide.querySelector('.swiper-slide-item');
    const ph = item && item.querySelector('.gallery-slide-ph');
    if (!ph) return false;
    const source = type === 'thumbs'
      ? { src: file.preview_image?.src || file.src, alt: file.alt }
      : file;
    const picture = this.createPictureElement(source, type);
    // The window IS the lazy mechanism here; native lazy-load fails to fire for
    // images inserted deep inside the translated swiper wrapper.
    picture.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
    ph.replaceWith(picture);
    return true;
  }

  // Populate slides around the active index in both swipers of a windowed gallery.
  populateSlideWindow(gallery, centerIndex) {
    if (!gallery._windowed) return;
    const files = gallery._files || [];
    const fill = (selector, radius, type) => {
      const wrapper = gallery.querySelector(selector);
      if (!wrapper) return false;
      const slides = wrapper.children;
      const from = Math.max(0, centerIndex - radius);
      const to = Math.min(slides.length - 1, centerIndex + radius);
      let swapped = false;
      for (let i = from; i <= to; i++) {
        if (this.populateSlide(slides[i], files[i], type === 'main' ? 'main' : 'thumbs')) swapped = true;
      }
      return swapped;
    };
    if (fill('.gallery__main .swiper-wrapper', 6, 'main')) {
      const key = gallery.getAttribute('data-gallery-featured') ||
                  gallery.getAttribute('data-gallery-lang') ||
                  gallery.getAttribute('data-gallery-variant');
      const inst = this.swiperInstances.get(key);
      if (inst) inst.main.update();
    }
  }

  async loadPhotoSwipe() {
    if (this._PhotoSwipe) return this._PhotoSwipe;
    const src = window.eveline_photoswipe_src;
    if (!src) {
      console.error('PhotoSwipe asset URL missing (window.eveline_photoswipe_src)');
      return null;
    }
    try {
      const module = await import(src);
      this._PhotoSwipe = module.default;
      return this._PhotoSwipe;
    } catch (e) {
      console.error('Failed to load PhotoSwipe:', e);
      return null;
    }
  }

  async openLightbox(gallery, mainSwiper, startSlideIndex) {
    const PhotoSwipe = await this.loadPhotoSwipe();
    if (!PhotoSwipe) return;

    const files = gallery._files || [];
    const images = [];
    const slideToImage = new Map();

    // Same predicate as slide building: only image files end up in the lightbox
    files.forEach((file, index) => {
      const isImage = file.media_type === 'image' || (typeof file.src === 'string' && file.src && !file.sources && !file.host);
      if (!isImage) return;
      slideToImage.set(index, images.length);
      images.push({
        src: file.src,
        width: file.width || file.preview_image?.width || 2000,
        height: file.height || file.preview_image?.height || 2000,
        alt: file.alt || '',
        slideIndex: index
      });
    });

    if (images.length === 0) return;

    const pswp = new PhotoSwipe({
      dataSource: images,
      index: slideToImage.get(startSlideIndex) ?? 0,
      showHideAnimationType: 'fade',
      bgOpacity: 0.95,
      wheelToZoom: true
    });

    // Keep the gallery in sync with what was last viewed in the lightbox
    pswp.on('close', () => {
      const current = images[pswp.currIndex];
      if (current && typeof current.slideIndex === 'number') {
        mainSwiper.slideTo(current.slideIndex, 0);
      }
    });

    pswp.init();
  }

  createVideoIframe(host, externalId) {
    const iframe = document.createElement('iframe');
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.frameBorder = '0';
    iframe.allowFullscreen = true;
    
    if (host === 'youtube') {
      const videoId = externalId;
      const playerId = `player-${videoId}-${Date.now()}`;
      iframe.id = playerId;
      
      // Never autoplay on init - we'll control it through handleVideoAutoplay
      // Always start muted to prevent sound on cloned slides in loop mode
      const params = new URLSearchParams({
        autoplay: 0,
        rel: 0,
        showinfo: 0,
        playsinline: 1,
        controls: 0,
        iv_load_policy: 3, // Hide annotations and info
        modestbranding: 1, // Minimal YouTube branding
        color: 'white',
        loop: 1, // Enable YouTube video loop
        playlist: videoId, // Required for loop to work
        mute: 1,
        enablejsapi: 1,
        origin: window.location.origin,
        widgetid: Math.floor(Math.random() * 1000),
        vq: 'hd1080' // High quality
      });
      
      iframe.src = `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.title = 'YouTube video player';
      
      // Store player reference
      this.youtubePlayers.set(playerId, { iframe, muted: this.youtubeVideosMuted, videoId });
      
    } else if (host === 'vimeo') {
      iframe.src = `https://player.vimeo.com/video/${externalId}`;
    }
    
    return iframe;
  }

  createToggleMuteButton() {
    const button = document.createElement('button');
    button.className = `sound-switch-btn ${this.videosMuted ? 'sound--off' : 'sound--on'}`;
    button.type = 'button';
    
    this.updateButtonContent(button, this.videosMuted);
    
    button.addEventListener('click', () => {
      this.toggleAllVideos();
    });

    return button;
  }

  updateButtonContent(button, isMuted) {
    button.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
    
    if (isMuted) {
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.63 3.63a.996.996 0 0 0 0 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 1 0 1.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0 0 14 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z"/>
        </svg>
      `;
    } else {
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 10v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71V6.41c0-.89-1.08-1.34-1.71-.71L7 9H4c-.55 0-1 .45-1 1zm13.5 2A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 4.45v.2c0 .38.25.71.6.85C17.18 6.53 19 9.06 19 12s-1.82 5.47-4.4 6.5c-.36.14-.6.47-.6.85v.2c0 .63.63 1.07 1.21.85C18.6 19.11 21 15.84 21 12s-2.4-7.11-5.79-8.4c-.58-.23-1.21.22-1.21.85z"/>
        </svg>
      `;
    }
  }

  updateToggleMuteButtonState(gallery, swiper) {
    const galleryIsActive = gallery.classList.contains('is-active');
    
    // First check: if currently displayed slide is a duplicate, return false
    const currentDisplayedSlide = swiper.slides[swiper.activeIndex];
    if (!currentDisplayedSlide || currentDisplayedSlide.classList.contains('swiper-slide-duplicate')) {
      gallery.setAttribute('data-video-slide-is-active', 'false');
      return;
    }
    
    // Get only original slides (not duplicates created by loop mode)
    const originalSlides = Array.from(swiper.slides).filter(slide => 
      !slide.classList.contains('swiper-slide-duplicate')
    );
    
    const activeSlide = originalSlides[swiper.realIndex];
    if (!activeSlide) {
      gallery.setAttribute('data-video-slide-is-active', 'false');
      return;
    }
    
    // Check if this specific slide has any video (YouTube or Shopify)
    const hasYoutubeVideo = activeSlide.querySelector('iframe[src*="youtube.com"]');
    const hasShopifyVideo = activeSlide.querySelector('video');

    if ((hasYoutubeVideo || hasShopifyVideo) && galleryIsActive) {
      gallery.setAttribute('data-video-slide-is-active', swiper.realIndex);
    } else {
      gallery.setAttribute('data-video-slide-is-active', 'false');
    }
  }

  toggleAllVideos() {
    this.videosMuted = !this.videosMuted;
    
    // Update all buttons
    const buttons = this.querySelectorAll('.sound-switch-btn');
    buttons.forEach(button => {
      button.className = `sound-switch-btn ${this.videosMuted ? 'sound--off' : 'sound--on'}`;
      this.updateButtonContent(button, this.videosMuted);
    });
    
    // Toggle mute on all YouTube iframes
    this.youtubePlayers.forEach((player, playerId) => {
      const iframe = player.iframe;
      if (iframe && iframe.contentWindow) {
        const message = this.videosMuted 
          ? '{"event":"command","func":"mute","args":""}' 
          : '{"event":"command","func":"unMute","args":""}';
        iframe.contentWindow.postMessage(message, '*');
      }
    });
    
    // Toggle mute on all Shopify video elements
    const videos = this.querySelectorAll('video');
    videos.forEach(video => {
      video.muted = this.videosMuted;
    });
  }

  getVideoEmbed(host, externalId) {
    // Legacy method - kept for compatibility
    const iframe = this.createVideoIframe(host, externalId);
    return iframe.outerHTML;
  }

  getShopifyImageUrl(baseUrl, width) {
    if (!baseUrl || typeof baseUrl !== 'string' || !width) return null;
    
    // Split by ? to separate query params
    const [path, query] = baseUrl.split('?');
    
    // Find last dot for extension
    const lastDot = path.lastIndexOf('.');
    if (lastDot === -1) return baseUrl;
    
    const basePath = path.substring(0, lastDot);
    const extension = path.substring(lastDot);
    
    // Add width in Shopify format
    return `${basePath}_${width}x${extension}${query ? '?' + query : ''}`;
  }

  createPictureElement(file, type = 'main') {
    const picture = document.createElement('picture');
    
    // Get config for this slider type
    const sliderConfig = type === 'thumbs' 
      ? this.galleryData.config?.slider_thumbs
      : this.galleryData.config?.slider_main;
    
    if (sliderConfig?.picture?.sizes) {
      // Generate source elements for each configured size
      sliderConfig.picture.sizes.forEach(sizeConfig => {
        const source = document.createElement('source');
        source.media = sizeConfig.media;
        
        // Generate srcset with 1x and 2x for retina displays
        const url1x = this.getShopifyImageUrl(file.src, sizeConfig.width);
        const url2x = this.getShopifyImageUrl(file.src, sizeConfig.width * 2);
        if (url1x) source.srcset = `${url1x} 1x, ${url2x || url1x} 2x`;

        picture.appendChild(source);
      });
    }
    
    // Fallback img with default size or original
    const img = document.createElement('img');
    const defaultSize = sliderConfig?.picture?.default_size;
    if (defaultSize) {
      // Also add srcset for fallback img
      const url1x = this.getShopifyImageUrl(file.src, defaultSize);
      const url2x = this.getShopifyImageUrl(file.src, defaultSize * 2);
      if (url1x) {
        img.srcset = `${url1x} 1x, ${url2x || url1x} 2x`;
        img.src = url1x;
      } else {
        img.src = typeof file.src === 'string' ? file.src : '';
      }
    } else {
      img.src = file.src;
    }
    img.alt = file.alt || '';
    img.loading = 'lazy';
    picture.appendChild(img);
    
    return picture;
  }
  
  disconnectedCallback() {
    // Cleanup swiper instances
    this.swiperInstances.forEach(({ main, thumbs }) => {
      if (main) main.destroy();
      if (thumbs) thumbs.destroy();
    });
    this.swiperInstances.clear();
  }
}

customElements.define('eveline-product-gallery', EvelineProductGallery);

class EvelineColorSwitcher extends HTMLElement {
  constructor() {
    super();
    this.optionName = null;
  }

  connectedCallback() {
    this.optionName = this.getAttribute('data-option-name');
    this.init();
  }

  init() {
    // Get elements
    this.listBar = this.querySelector('.switch-list-bar');
    this.listContainer = this.querySelector('.switch-list');
    this.gridOptions = this.querySelectorAll('.switch-grid-option');
    this.listOptions = this.querySelectorAll('.switch-list-option');

    // Make elements keyboard accessible
    this.setupKeyboardAccessibility();

    // Bind events
    if (this.listBar && this.listContainer) {
      this.listBar.addEventListener('click', (e) => this.toggleDropdown(e));
      this.listBar.addEventListener('keydown', (e) => this.handleListBarKeyDown(e));
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => this.handleOutsideClick(e));
    }

    // Handle grid option clicks
    this.gridOptions.forEach(option => {
      option.addEventListener('click', (e) => this.handleOptionClick(e, option));
      option.addEventListener('keydown', (e) => this.handleOptionKeyDown(e, option));
    });

    // Handle list option clicks
    this.listOptions.forEach(option => {
      option.addEventListener('click', (e) => this.handleOptionClick(e, option));
      option.addEventListener('keydown', (e) => this.handleOptionKeyDown(e, option));
    });
    
    // Initialize title value with selected option
    const selectedOption = this.querySelector('.switch-grid-option.is-selected, .switch-list-option.is-selected');
    if (selectedOption) {
      const value = selectedOption.getAttribute('data-option-value');
      const titleValue = this.querySelector('.switchers-title__value');
      if (titleValue && value) {
        titleValue.textContent = value;
      }
    }
  }

  setupKeyboardAccessibility() {
    // Make list bar focusable
    if (this.listBar) {
      this.listBar.setAttribute('tabindex', '0');
      this.listBar.setAttribute('role', 'button');
      this.listBar.setAttribute('aria-haspopup', 'listbox');
      this.listBar.setAttribute('aria-expanded', 'false');
    }

    // Make grid options focusable
    this.gridOptions.forEach((option, index) => {
      option.setAttribute('tabindex', '0');
      option.setAttribute('role', 'radio');
      option.setAttribute('aria-checked', option.classList.contains('is-selected') ? 'true' : 'false');
    });

    // Make list options focusable
    this.listOptions.forEach((option, index) => {
      option.setAttribute('tabindex', '-1');
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', option.classList.contains('is-selected') ? 'true' : 'false');
    });

    // Set list options container role
    const listOptionsContainer = this.querySelector('.switch-list-options');
    if (listOptionsContainer) {
      listOptionsContainer.setAttribute('role', 'listbox');
    }
  }

  handleListBarKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleDropdown(e);
    } else if (e.key === 'Escape') {
      this.closeDropdown();
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!this.listContainer.classList.contains('is-open')) {
        this.toggleDropdown(e);
      }
      // Focus first option
      if (this.listOptions.length > 0) {
        this.focusListOption(0);
      }
    }
  }

  handleOptionKeyDown(e, option) {
    // Handle selection
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleOptionClick(e, option);
      return;
    }

    // Handle navigation in grid mode
    if (option.classList.contains('switch-grid-option')) {
      this.handleGridKeyNavigation(e, option);
    }

    // Handle navigation in list mode
    if (option.classList.contains('switch-list-option')) {
      this.handleListKeyNavigation(e, option);
    }
  }

  handleGridKeyNavigation(e, currentOption) {
    const options = Array.from(this.gridOptions);
    const currentIndex = options.indexOf(currentOption);

    let nextIndex = currentIndex;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        nextIndex = (currentIndex + 1) % options.length;
        break;
      case 'ArrowLeft':
        e.preventDefault();
        nextIndex = (currentIndex - 1 + options.length) % options.length;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = options.length - 1;
        break;
    }

    if (nextIndex !== currentIndex) {
      options[nextIndex].focus();
    }
  }

  handleListKeyNavigation(e, currentOption) {
    const options = Array.from(this.listOptions);
    const currentIndex = options.indexOf(currentOption);

    let nextIndex = currentIndex;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        nextIndex = Math.min(currentIndex + 1, options.length - 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = options.length - 1;
        break;
      case 'Escape':
        e.preventDefault();
        this.closeDropdown();
        this.listBar?.focus();
        return;
      case 'Tab':
        this.closeDropdown();
        return;
    }

    if (nextIndex !== currentIndex) {
      this.focusListOption(nextIndex);
    }
  }

  focusListOption(index) {
    const options = Array.from(this.listOptions);
    if (options[index]) {
      options.forEach(opt => opt.setAttribute('tabindex', '-1'));
      options[index].setAttribute('tabindex', '0');
      options[index].focus();
    }
  }

  toggleDropdown(e) {
    e.stopPropagation();
    const isOpen = this.listContainer.classList.toggle('is-open');
    
    if (this.listBar) {
      this.listBar.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  closeDropdown() {
    if (this.listContainer) {
      this.listContainer.classList.remove('is-open');
      if (this.listBar) {
        this.listBar.setAttribute('aria-expanded', 'false');
      }
    }
  }

  handleOutsideClick(e) {
    if (this.listContainer && !this.listContainer.contains(e.target)) {
      this.closeDropdown();
    }
  }

  handleOptionClick(e, option) {
    const value = option.getAttribute('data-option-value');

    if (!value) return;

    const variantId = option.getAttribute('data-variant');

    // Update selection state
    this.updateSelection(value);

    // Close dropdown if in list mode
    this.closeDropdown();

    // Emit custom event
    this.dispatchEvent(new CustomEvent('option-change', {
      bubbles: true,
      detail: {
        optionName: this.optionName,
        value: value,
        variantId: variantId
      }
    }));
  }

  updateSelection(value) {
    // Update grid options
    this.gridOptions.forEach(option => {
      const isSelected = option.getAttribute('data-option-value') === value;
      if (isSelected) {
        option.classList.add('is-selected');
      } else {
        option.classList.remove('is-selected');
      }
      option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    // Update list options
    this.listOptions.forEach(option => {
      const isSelected = option.getAttribute('data-option-value') === value;
      if (isSelected) {
        option.classList.add('is-selected');
      } else {
        option.classList.remove('is-selected');
      }
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    // Update list bar display
    const selectedOption = Array.from(this.listOptions).find(
      option => option.getAttribute('data-option-value') === value
    );

    if (selectedOption && this.listBar) {
      const barImage = this.listBar.querySelector('.switch-list-bar__image');
      const barName = this.listBar.querySelector('.switch-list-bar__name');
      const optionImage = selectedOption.querySelector('.switch-list-option__image');
      const optionName = selectedOption.querySelector('.switch-list-option__name');

      if (barImage && optionImage) {
        barImage.style.cssText = optionImage.style.cssText;
      }
      if (barName && optionName) {
        barName.textContent = optionName.textContent;
      }
      
      // Update data attributes from selected option
      const variantId = selectedOption.getAttribute('data-variant');
      const inventoryQty = selectedOption.getAttribute('data-variant-inventory-qty');
      if (variantId) {
        this.listBar.setAttribute('data-variant', variantId);
      }
      if (inventoryQty !== null) {
        this.listBar.setAttribute('data-variant-inventory-qty', inventoryQty);
      } else {
        this.listBar.removeAttribute('data-variant-inventory-qty');
      }
    }
    
    // Update switchers title value
    const titleValue = this.querySelector('.switchers-title__value');
    if (titleValue) {
      titleValue.textContent = value;
    }
  }

  disconnectedCallback() {
    // Cleanup event listeners
    if (this.listBar) {
      this.listBar.removeEventListener('click', this.toggleDropdown);
    }
    document.removeEventListener('click', this.handleOutsideClick);
  }
}

customElements.define('eveline-color-switcher', EvelineColorSwitcher);


class EvelineReviews extends HTMLElement {
  constructor() {
    super();
    this.itemsToLoadOnClick = parseInt(this.dataset.itemsToLoadOnClick) || 5;
    this.items = [];
    this.loadMoreButton = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.items = Array.from(this.querySelectorAll('.eveline-reviews__item'));
    this.loadMoreButton = this.querySelector('.eveline-reviews__button--more');
    
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', () => this.loadMoreItems());
    }

    this.checkIfAllVisible();
  }

  loadMoreItems() {
    const visibleItems = this.items.filter(item => item.classList.contains('is-visible'));
    const visibleCount = visibleItems.length;
    
    // Get next batch of items
    const itemsToShow = this.items.slice(visibleCount, visibleCount + this.itemsToLoadOnClick);
    
    if (itemsToShow.length === 0) return;
    
    // Store first new item for focus
    const firstNewItem = itemsToShow[0];
    
    // Make them visible
    itemsToShow.forEach(item => {
      item.classList.add('is-visible');
    });

    // Set focus on first new item for accessibility
    if (firstNewItem) {
      setTimeout(() => {
        firstNewItem.focus();
        firstNewItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }

    this.checkIfAllVisible();
  }

  checkIfAllVisible() {
    const visibleItems = this.items.filter(item => item.classList.contains('is-visible'));
    
    if (visibleItems.length >= this.items.length) {
      this.hideLoadMoreButton();
    }
  }

  hideLoadMoreButton() {
    if (this.loadMoreButton) {
      this.loadMoreButton.style.display = 'none';
    }
  }

  disconnectedCallback() {
    if (this.loadMoreButton) {
      this.loadMoreButton.removeEventListener('click', () => this.loadMoreItems());
    }
  }
}

customElements.define('eveline-reviews', EvelineReviews);


// Rating links ("Reviews", "FAQ"): scroll to the target section, hide links whose target is absent
(function() {
  if (window.__evelineRatingLinksBound) return;
  window.__evelineRatingLinksBound = true;

  const LINK_SELECTOR = '.eveline-rating__link[data-scroll-to]';

  const syncLinks = () => {
    document.querySelectorAll(LINK_SELECTOR).forEach((link) => {
      link.hidden = !document.querySelector(link.dataset.scrollTo);
    });
  };

  document.addEventListener('click', (e) => {
    const link = e.target.closest(LINK_SELECTOR);
    if (!link) return;

    const target = document.querySelector(link.dataset.scrollTo);
    if (!target) return;

    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const focusTarget = (link.hash && document.querySelector(link.hash)) || target;
    focusTarget.focus({ preventScroll: true });
  });

  syncLinks();
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((eventName) => {
    document.addEventListener(eventName, () => setTimeout(syncLinks, 100));
  });
})();


(function() {
    const productGiftsBlock = document.querySelector('[data-block-type="eveline-product-gifts"]');
    
    if(!productGiftsBlock) return;

    const giftsContainer = productGiftsBlock.querySelector('[data-product-gifts]');
    
    if(!giftsContainer) return;

    // Use current page URL as base
    const baseUrl = window.location.pathname;

    function updateGifts() {
        if(!baseUrl) return;

        const url = `${baseUrl}?view=eveline-gifts`;

        fetch(url, {
            method: 'GET'
        })
        .then(response => {
            if(!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.text();
        })
        .then(html => {
            const tmp = document.createElement('div');
            tmp.innerHTML = html;
            const newContent = tmp.querySelector('[data-product-gifts]');

            if(newContent && giftsContainer) {
                giftsContainer.innerHTML = newContent.innerHTML;
            }
        })
        .catch(error => {
            console.error('Error updating product gifts:', error);
        });
    }

    // Listen for cart updates to refresh gifts
    window.addEventListener('theme:cart:updated', () => {
        console.log('Cart updated, refreshing gifts...');
        updateGifts();
    });
})();

// Universal variant availability updater - works for all products (with or without custom gallery)
(() => {
    // Function to update data-variant-available attribute on product-buy-buttons-element
    const updateVariantAvailability = (variant) => {
        if (!variant) return;
        
        const buyButtonsElement = document.querySelector('product-buy-buttons-element');
        if (buyButtonsElement) {
            const available = variant.available || variant.available === true;
            buyButtonsElement.setAttribute('data-variant-available', available ? '1' : '0');
        }
    };

    // Listen for all theme:product:*:variantChanged events (from standard theme.js)
    // This will catch variant changes from standard product-options-element (used in sets/bundles)
    const variantChangedPattern = /^theme:product:.+:variantChanged$/;
    
    window.addEventListener('message', (event) => {
        // Handle events dispatched as CustomEvents (which appear as window events)
        if (event.type && variantChangedPattern.test(event.type)) {
            const variant = event.detail?.variant;
            updateVariantAvailability(variant);
        }
    });

    // Also capture direct window events
    const originalDispatchEvent = window.dispatchEvent;
    window.dispatchEvent = function(event) {
        if (event instanceof CustomEvent && variantChangedPattern.test(event.type)) {
            const variant = event.detail?.variant;
            updateVariantAvailability(variant);
        }
        return originalDispatchEvent.apply(this, arguments);
    };
})();


/* --------------------------------------------------------------------------
 * Bundlex: bundle add-to-cart split off from the theme's add-to-cart.
 * ------------------------------------------------------------------------ */
(function () {
  var BUTTON_CLASS = 'bundlex-add';
  var MARKER = 'bx-product-form';
  var CARRIAGE = 'properties[_bundlex]';
  var FIELDS = '[name="' + MARKER + '"], [name="' + CARRIAGE + '"]';
  var CART_FORM = 'form[action*="/cart/add"]';

  function cartElement() {
    return customElements.get('cart-element');
  }

  function cartSections() {
    var Cart = cartElement();
    return Cart && Cart.sections && Cart.sections.length ? Cart.sections : ['cart-drawer'];
  }

  function productForm() {
    var widget = document.querySelector('[data-bundlex-widget]');
    return (widget && widget.closest(CART_FORM)) || document.querySelector('form.product-form');
  }

  function detachFields(form) {
    var fields = Array.prototype.slice.call(form.querySelectorAll(FIELDS));
    fields.forEach(function (input) { input.remove(); });

    return fields;
  }

  function reattachFields(form, fields) {
    fields.forEach(function (input) {
      if (!form.querySelector('[name="' + input.name + '"]')) form.appendChild(input);
    });
  }

  function submitEvent(submitter) {
    if (typeof SubmitEvent === 'function') {
      return new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter: submitter });
    }

    var event = new Event('submit', { bubbles: true, cancelable: true });
    Object.defineProperty(event, 'submitter', { value: submitter });

    return event;
  }

  function onThemeAddToCart(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var button = target.closest('.product-buy-buttons--primary');
    if (!button) return;

    var form = button.form || button.closest(CART_FORM);
    if (!form || !form.matches(CART_FORM)) return;

    if (!form.querySelector(FIELDS)) return;

    event.preventDefault();
    event.stopPropagation();

    var fields = detachFields(form);

    try {
      form.dispatchEvent(submitEvent(button));
    } finally {
      reattachFields(form, fields);
    }
  }

  window.addEventListener('click', onThemeAddToCart, true);

  function activeButton(fallback) {
    return document.querySelector('.bx-offer-wrap.bx-active .' + BUTTON_CLASS)
      || (fallback && fallback.isConnected ? fallback : null);
  }

  function createButton(label) {
    var button = document.createElement('button');

    button.type = 'button';
    button.className = BUTTON_CLASS;
    button.setAttribute('data-item', 'button-1');
    button.setAttribute('data-text', label);
    button.setAttribute('aria-busy', 'false');
    button.innerHTML = '<span class="bundlex-add__text"></span>'
      + '<span class="bundlex-add__spinner">'
      + '<svg class="icon--root icon--spinner"><use xlink:href="#loading-spinner"></use></svg>'
      + '</span>';
    button.querySelector('.bundlex-add__text').textContent = label;

    return button;
  }

  function addButtons() {
    var widget = document.querySelector('[data-bundlex-widget]');
    var label = widget && widget.getAttribute('data-add-label');
    if (!label) return;

    document.querySelectorAll('.bx-offer-main').forEach(function (offer) {
      if (!offer.querySelector('.' + BUTTON_CLASS)) {
        offer.appendChild(createButton(label));
      }
    });
  }

  function addBundleToCart(button) {
    var form = productForm();
    var Cart = cartElement();
    if (!form || !Cart) return;

    var params = new URLSearchParams(new FormData(form));

    if (!params.has(MARKER)) {
      console.warn('[eveline] Bundlex has not marked the product form, adding the bundle is not possible.');
      return;
    }

    if (button) button.setAttribute('aria-busy', 'true');

    Promise.resolve()
      .then(function () {
        return Cart.addItems({
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString() + '&sections=' + cartSections().join(',')
        }, button);
      })
      .catch(function (error) {
        console.error('[eveline] Bundlex add to cart failed:', error);
      })
      .then(function () {
        var current = activeButton(button);
        if (current) current.setAttribute('aria-busy', 'false');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.' + BUTTON_CLASS);
    if (!button) return;

    setTimeout(function () { addBundleToCart(activeButton(button)); }, 50);
  });

  function watchWidget() {
    var widget = document.querySelector('[data-bundlex-widget]');
    if (!widget) return;

    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        addButtons();
      });
    }).observe(widget, { childList: true, subtree: true });

    addButtons();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchWidget, { once: true });
  } else {
    watchWidget();
  }
})();
